import {
  Alert,
  Box,
  Button,
  Heading,
  HStack,
  IAlertProps,
  Pressable,
  Text,
  useToken,
  VStack,
} from 'native-base';
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Easing, Platform, StyleSheet } from 'react-native';

import {
  NoticeAdd,
  NoticeAlert,
  NoticeInfo,
  NoticeSubtract,
  NoticeSuccess,
} from 'app/components/icons';
import { NoticeTheme } from 'app/generated/hygraph';
import type { NoticeAlertStatus } from 'app/services/NoticeHelper';
import { tID } from 'app/services/TestHelper';

import CollapsibleContent from './CollapsibleContent';

const styles = StyleSheet.create({
  toggleContainer: {
    height: 16,
    width: 16,
  },
  toggleIcon: {
    position: 'absolute',
  },
});

type NoticeVisualStyle = { bg: string; border: string; textColor: string; iconColor: string };

const themeStyles: Record<NoticeTheme, NoticeVisualStyle> = {
  [NoticeTheme.Default]: {
    bg: 'notice.info.bg',
    border: 'notice.info.border',
    textColor: 'notice.info.textColor',
    iconColor: 'notice.info.iconColor',
  },
  [NoticeTheme.Info]: {
    bg: 'notice.info.bg',
    border: 'notice.info.border',
    textColor: 'notice.info.textColor',
    iconColor: 'notice.info.iconColor',
  },
  [NoticeTheme.Warn]: {
    bg: 'notice.warning.bg',
    border: 'notice.warning.border',
    textColor: 'notice.warning.textColor',
    iconColor: 'notice.warning.iconColor',
  },
  [NoticeTheme.Danger]: {
    bg: 'notice.error.bg',
    border: 'notice.error.border',
    textColor: 'notice.error.textColor',
    iconColor: 'notice.error.iconColor',
  },
  [NoticeTheme.MessageLight]: {
    bg: 'notice.messageLight.bg',
    border: 'notice.messageLight.border',
    textColor: 'notice.messageLight.textColor',
    iconColor: 'notice.messageLight.iconColor',
  },
  [NoticeTheme.MessageDark]: {
    bg: 'notice.messageDark.bg',
    border: 'notice.messageDark.border',
    textColor: 'notice.messageDark.textColor',
    iconColor: 'notice.messageDark.iconColor',
  },
};

const statusStyles: Record<NonNullable<IAlertProps['status']>, NoticeVisualStyle> = {
  info: themeStyles[NoticeTheme.Info],
  success: {
    bg: 'notice.success.bg',
    border: 'notice.success.border',
    textColor: 'notice.success.textColor',
    iconColor: 'notice.success.iconColor',
  },
  warning: themeStyles[NoticeTheme.Warn],
  error: themeStyles[NoticeTheme.Danger],
  messageLight: themeStyles[NoticeTheme.MessageLight],
  messageDark: themeStyles[NoticeTheme.MessageDark],
};

const themeToAlertStatus: Record<NoticeTheme, NonNullable<IAlertProps['status']>> = {
  [NoticeTheme.Default]: 'info',
  [NoticeTheme.Info]: 'info',
  [NoticeTheme.Warn]: 'warning',
  [NoticeTheme.Danger]: 'error',
  [NoticeTheme.MessageLight]: 'info',
  [NoticeTheme.MessageDark]: 'info',
};

export type NoticeCardProps = Omit<IAlertProps, 'status'> & {
  children?: ReactNode;
  title?: string;
  status?: NoticeAlertStatus;
  theme?: NoticeTheme;
  variant?: 'default' | 'simple' | 'mini';
  initialOpen?: boolean;
  collapsible?: boolean;
  action?: { label: string; onPress: () => void };
  onExpand?: () => void;
};

export default function NoticeCard({
  children,
  title,
  status = 'info',
  theme,
  variant = 'default',
  initialOpen = true,
  collapsible = false,
  action,
  onExpand,
  ...props
}: NoticeCardProps) {
  const isSimpleOrMini = variant === 'simple' || variant === 'mini';
  const effectiveCollapsible = isSimpleOrMini ? false : collapsible;

  const [open, setOpen] = useState(false);
  const toggleOpen = () => setOpen((prev) => !prev);
  useEffect(() => {
    if (effectiveCollapsible && initialOpen) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const alertStatus = theme ? themeToAlertStatus[theme] : status;
  const visual = theme ? themeStyles[theme] : statusStyles[status];
  const textColor = useToken('colors', visual.textColor);
  const iconColor = useToken('colors', visual.iconColor);

  const crossfadeAnim = useRef(
    new Animated.Value(effectiveCollapsible && !initialOpen ? 0 : 1)
  ).current;

  const animateCrossfade = (toOpen: boolean) => {
    Animated.timing(crossfadeAnim, {
      duration: 200,
      easing: Easing.linear,
      toValue: toOpen ? 1 : 0,
      useNativeDriver: Platform.OS !== 'web' && process.env.NODE_ENV !== 'test',
    }).start();
  };

  const isHtmlElement = (child: unknown): child is React.ReactElement<{ html: string }> =>
    React.isValidElement(child) && typeof (child.props as { html: string })?.html === 'string';

  const $children = useMemo(() => {
    if (isSimpleOrMini) return null;

    return React.Children.map(children, (child) => {
      if (child == null || child === false) return null;

      if (isHtmlElement(child)) {
        return child; // render as-is
      }

      if (React.isValidElement(child)) {
        return child;
      }

      return (
        <Text color={visual.textColor} fontSize="sm">
          {child}
        </Text>
      );
    });
  }, [children, isSimpleOrMini, visual.textColor]);

  const handleToggle = () => {
    const nextOpen = !open;
    if (nextOpen && onExpand) onExpand();
    toggleOpen();
    animateCrossfade(nextOpen);
  };

  const renderIcon = () => {
    switch (alertStatus) {
      case 'error':
        return <NoticeAlert color={iconColor} size={16} />;
      case 'warning':
        return <NoticeAlert color={iconColor} size={16} />;
      case 'success':
        return <NoticeSuccess color={iconColor} size={16} />;
      case 'info':
        return <NoticeInfo color={iconColor} size={16} />;
      case 'messageLight':
        return <NoticeInfo color={iconColor} size={16} />;
      case 'messageDark':
        return <NoticeInfo color={iconColor} size={16} />;
      default:
        return <NoticeInfo color={iconColor} size={16} />;
    }
  };

  const renderToggleIcon = () => (
    <Box marginBottom="auto" marginTop="0" style={styles.toggleContainer}>
      <Animated.View style={[styles.toggleIcon, { opacity: crossfadeAnim }]}>
        <NoticeSubtract color={iconColor} size={16} />
      </Animated.View>
      <Animated.View
        style={[
          styles.toggleIcon,
          {
            opacity: crossfadeAnim.interpolate({
              inputRange: [0, 1],
              outputRange: [1, 0],
            }),
          },
        ]}
      >
        <NoticeAdd color={iconColor} size={16} />
      </Animated.View>
    </Box>
  );

  const alertProps = {
    bg: visual.bg,
    borderColor: visual.border,
    borderRadius: 4,
    borderWidth: 1,
    p: variant === 'mini' ? 2 : 4,
    shadow: 'notice',
    status: alertStatus,
  };

  const fontWeight = isSimpleOrMini ? 'normal' : 'bold';

  if (title) {
    const titleContent = (
      <HStack alignItems="flex-start" flex={1} space="4">
        <Box borderRightColor={visual.border} borderRightWidth={1} height="100%" pr="3">
          <Box marginY="auto">{renderIcon()}</Box>
        </Box>
        <HStack flex={1} flexShrink={1} justifyContent="space-between">
          <Box flex={1}>
            <VStack flex={1} w="full">
              <Heading color={textColor} flexShrink={1} fontSize="xs" fontWeight={fontWeight}>
                {title}
              </Heading>
              {($children || action) && (
                <CollapsibleContent collapsed={!open} collapsible={effectiveCollapsible} pt="1">
                  {$children}
                  {action && (
                    <Button
                      mt={$children ? '4' : '0'}
                      variant="noticeAction"
                      onPress={action.onPress}
                    >
                      {action.label}
                    </Button>
                  )}
                </CollapsibleContent>
              )}
            </VStack>
          </Box>
          {effectiveCollapsible && renderToggleIcon()}
        </HStack>
      </HStack>
    );

    return (
      <Alert
        alignItems="flex-start"
        testID={tID('Alert')}
        {...alertProps}
        {...(variant === 'mini' && { alignSelf: 'flex-start' })}
        {...props}
      >
        {effectiveCollapsible ? (
          <Pressable w="full" onPress={handleToggle}>
            {titleContent}
          </Pressable>
        ) : (
          <Box w="full">{titleContent}</Box>
        )}
      </Alert>
    );
  }

  if ($children) {
    return (
      <Alert alignItems="flex-start" testID={tID('Alert')} {...alertProps} {...props}>
        <HStack alignItems="flex-start" space="4" w="full">
          <Box alignSelf="stretch" borderRightColor={visual.border} borderRightWidth={1} pr="3">
            <Box marginY="auto">{renderIcon()}</Box>
          </Box>
          <VStack flex={1} flexShrink={1}>
            {$children}
            {action && (
              <Button mt="4" variant="noticeAction" onPress={action.onPress}>
                {action.label}
              </Button>
            )}
          </VStack>
        </HStack>
      </Alert>
    );
  }

  return <Alert alignItems="flex-start" testID={tID('Alert')} {...alertProps} {...props} />;
}
